"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, ShieldCheck, Clock, Users, PhoneCall, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const propertyTypes = [{
  id: "offices",
  title: "Office complexes & commercial suites",
  description: "Targeted structural drying and containment so tenant operations continue without whole-floor shutdowns."
}, {
  id: "hoa",
  title: "Condominiums & HOA communities",
  description: "Protection for shared common elements, riser breaks, and multi-unit vertical water travels."
}, {
  id: "retail",
  title: "Retail centers & storefronts",
  description: "Rapid water extraction and immediate deodorization to prevent inventory losses and customer disruption."
}, {
  id: "education",
  title: "Educational & institutional facilities",
  description: "Off-hours air scrubbing, antimicrobial sanitization, and documented clearances for rapid re-occupancy."
}];
const liaisonPoints = [{
  id: "p1",
  label: "Direct single-point contact",
  detail: "Direct contact with local crew leads, bypassing remote call centers."
}, {
  id: "p2",
  label: "Insurance coordination",
  detail: "Itemized line-item documentation directly aligned with your property adjuster."
}, {
  id: "p3",
  label: "Life-safety compliance",
  detail: "Containment barriers and negative air filtration meeting regional building codes."
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.35,
      ease: "easeOut"
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar Motif */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-10 border-b border-border text-xs font-mono text-muted-foreground uppercase tracking-wider">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span>Commercial Dispatch • MD / DC / NoVA</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Response: Under 60 Minutes</span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">Direct Crew Coordination</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Text & Structured Info */}
          <motion.div {...animationProps} className="lg:col-span-7 flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted text-foreground w-fit text-xs font-semibold tracking-wide uppercase">
              <Building2 className="size-3.5 text-primary" />
              <span>Multi-Facility Mitigation</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-default text-foreground leading-tight">
              Rapid commercial restoration engineered to protect operations and common property.
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              When a pipe bursts in an office high-rise or a fire triggers multi-level sprinkler discharge in a condominium, minutes dictate the boundary between quick recovery and prolonged tenant disruption. TriServe mobilizes direct local technicians across Maryland, Washington DC, and Northern Virginia to contain water migration, sanitize air pathways, and protect shared structural assets.
            </p>

            <div className="p-5 rounded-lg border border-border bg-muted/50 my-2">
              <h3 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                <span>Direct board and facilities coordination</span>
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We work directly with property managers, HOA trustees, and corporate facilities directors. You deal straight with the lead technicians on-site who manage containment, provide daily moisture telemetry reports, and interface directly with commercial insurance adjusters.
              </p>
            </div>

            {/* Property Types Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {propertyTypes.map((item, index) => <div key={item.id} data-index={index} className="p-4 rounded-md border border-border bg-card hover:border-primary/50 transition-colors">
                  <h4 className="text-sm font-semibold text-foreground mb-1">{item.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                </div>)}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 shadow-sm">
                <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-semibold px-5">
                <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                  <span>Schedule Site Inspection</span>
                  <ArrowRight className="size-4 text-muted-foreground" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Photography & Liaison Card */}
          <motion.div {...animationProps} transition={shouldReduceMotion ? {} : {
          duration: 0.35,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Image Container with Spec Framing */}
            <div className="relative rounded-lg border border-border overflow-hidden bg-muted group shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hallway_scrubbers.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hallway_scrubbers.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hallway_scrubbers.png-wvc-sizes" alt="TriServe technicians positioning commercial HEPA air filtration equipment in a multi-story building hallway" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

              {/* Inset Operational Spec Tag */}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs text-white/90 bg-background/20 backdrop-blur-md px-3 py-2 rounded border border-white/20">
                <div className="flex items-center gap-1.5 font-mono">
                  <span className="size-1.5 rounded-full bg-emerald-400" />
                  <span>Active HEPA 2000 CFM Air Scrubbing</span>
                </div>
                <span className="font-mono text-[11px] opacity-80">Hallway Containment</span>
              </div>
            </div>

            {/* Structured Liaison Card */}
            <Card className="border border-border bg-card shadow-sm">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <Users className="size-4 text-primary" />
                  <span>The TriServe Commercial Guarantee</span>
                </div>

                <ul className="space-y-3.5">
                  {liaisonPoints.map((point, index) => <li key={point.id} data-index={index} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-foreground leading-none mb-1">
                          {point.label}
                        </p>
                        <p className="text-xs text-muted-foreground leading-normal">
                          {point.detail}
                        </p>
                      </div>
                    </li>)}
                </ul>

                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="size-3.5 text-primary" />
                    <span>24/7 Rapid Mobilization</span>
                  </span>
                  <span className="font-mono">MD • DC • NOVA</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}